import { getApiOrigin } from '@utils/apiOrigin';
import { OIG_ADDON_BLOCKS } from '@services/payment';

const PLANS_PATH = '/api/v1/pricing-tiers';
// Live company OIG capacity (MCM ledger via Rules). Pricing-tiers is catalog-only.
const OIG_ENTITLEMENT_PATH = '/api/v1/oig/entitlement';
const COMPANY_OIG_POOL_PATH = '/api/v1/company/oig-pool';
const ADDON_PLAN_FILTERS = JSON.stringify({
  plan_type: 'addon',
  sku_name: 'SKU.Unicomply.Rules',
});

export type AddonSubscriptionState = 'active' | 'not_activated' | 'coming_soon' | string;

export interface CompanyAddonUsage {
  used: number;
  total: number;
  remaining?: number;
}

export interface CompanyAddon {
  id: number | string;
  addon_key?: string;
  name: string;
  description?: string | null;
  subscription_state: AddonSubscriptionState;
  current_tier_name?: string | null;
  usage?: CompanyAddonUsage | null;
  pending_request_count?: number | null;
  starting_price?: number | string | null;
}

export interface CompanyAddonSubscriptionDetail {
  status?: string;
  tier_id?: number | null;
  tier_name?: string | null;
  tier_price?: number | string | null;
  renewal_date?: string | null;
  usage?: CompanyAddonUsage | null;
}

export const OIG_CHECKS_ADDON_KEY = 'oig_checks';

const OIG_PLAN_IDS = new Set<number>(OIG_ADDON_BLOCKS.map((block) => block.planId));

export interface CompanyAddonTier {
  id: number | string;
  tier_key?: string;
  name?: string;
  capacity?: number | null;
  price_annual?: number | string | null;
  sort_order?: number;
  purchasable?: boolean;
}

export interface CompanyAddonDetail extends CompanyAddon {
  tiers?: CompanyAddonTier[];
  subscription?: CompanyAddonSubscriptionDetail | null;
  aggregate?: {
    total_needed?: number;
    remaining?: number;
    pool_remaining?: number;
    prorated_charge_today?: number | string | null;
  } | null;
  requests?: Array<{ id?: number | string; created_at?: string; requester_name?: string }>;
}

function addonKey(plan: Record<string, unknown>): string | undefined {
  const details = plan.plan_details;
  const entry = Array.isArray(details) ? details[0] : details;
  if (entry && typeof entry === 'object') {
    const key = (entry as { addon?: unknown }).addon;
    if (typeof key === 'string' && key.trim()) return key.trim();
  }
  if (OIG_PLAN_IDS.has(Number(plan.id))) return OIG_CHECKS_ADDON_KEY;
  return undefined;
}

function toTier(plan: Record<string, unknown>, index: number): CompanyAddonTier {
  const packages = plan.content_package_details;
  const cp = Array.isArray(packages) ? (packages[0] as Record<string, unknown> | undefined) : undefined;
  const parsed = cp?.feature_count == null ? null : Number(cp.feature_count);
  const capacity = parsed != null && Number.isFinite(parsed) ? parsed : null;
  return {
    id: plan.id as number | string,
    tier_key: String(plan.id),
    name: capacity != null ? `+${capacity}` : String(plan.name || ''),
    capacity,
    price_annual: (plan.price as number | string | null) ?? null,
    sort_order: Number(plan.display_order ?? index + 1) || index + 1,
    purchasable: true,
  };
}

function tiersFor(plan: Record<string, unknown>, plans: Record<string, unknown>[]): CompanyAddonTier[] {
  const key = addonKey(plan);
  const group = key ? plans.filter((row) => addonKey(row) === key) : [plan];
  return group
    .sort((a, b) => Number(a.display_order ?? 0) - Number(b.display_order ?? 0))
    .map(toTier);
}

function toAddon(plan: Record<string, unknown>): CompanyAddon {
  const id = plan.id as number | string;
  return {
    id,
    addon_key: addonKey(plan),
    name: String(plan.name || ''),
    description: (plan.description as string | null) ?? null,
    subscription_state: 'not_activated',
    starting_price: (plan.price as number | string | null) ?? null,
  };
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  return {
    Authorization: token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json',
  };
}

interface OigCompanyState {
  /** Purchased add-on checks (MCM purchased_addon_count); null when unknown. */
  purchased: number | null;
  usage: CompanyAddonUsage;
}

function toCount(value: unknown): number | undefined {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

async function fetchJson(url: string): Promise<unknown> {
  const response = await fetch(url, { headers: authHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

/**
 * Real company OIG state: entitlement (needs facility) gives purchased count + pool usage;
 * company/oig-pool is a usage-only fallback. Returns null on failure so the catalog still renders.
 */
async function fetchOigCompanyState(facilityId?: number | string): Promise<OigCompanyState | null> {
  if (facilityId !== undefined && facilityId !== null && String(facilityId) !== '') {
    try {
      const params = new URLSearchParams({ facility_id: String(facilityId) });
      const data = (await fetchJson(
        `${getApiOrigin()}${OIG_ENTITLEMENT_PATH}?${params.toString()}`,
      )) as Record<string, unknown>;
      const total = toCount(data?.limit);
      if (total !== undefined) {
        const used = toCount(data.used) ?? 0;
        return {
          purchased: toCount(data.purchased_addon_count) ?? 0,
          usage: { total, used, remaining: toCount(data.remaining) ?? Math.max(0, total - used) },
        };
      }
    } catch {
      // fall through to company pool
    }
  }
  try {
    const payload = (await fetchJson(`${getApiOrigin()}${COMPANY_OIG_POOL_PATH}`)) as {
      data?: Record<string, unknown>;
    };
    const pool = payload?.data;
    const total = toCount(pool?.total);
    if (!pool || total === undefined) return null;
    const used = toCount(pool.used) ?? 0;
    return {
      purchased: null,
      usage: { total, used, remaining: toCount(pool.remaining) ?? Math.max(0, total - used) },
    };
  } catch {
    return null;
  }
}

function withOigState<T extends CompanyAddon>(addon: T, state: OigCompanyState | null): T {
  if (!state || addon.addon_key !== OIG_CHECKS_ADDON_KEY) return addon;
  const active = (state.purchased ?? 0) > 0;
  return {
    ...addon,
    subscription_state: active ? 'active' : addon.subscription_state,
    current_tier_name: active ? `+${state.purchased} checks` : addon.current_tier_name ?? null,
    usage: state.usage,
  };
}

async function fetchAddonPlans(): Promise<Record<string, unknown>[]> {
  const response = await fetch(
    `${getApiOrigin()}${PLANS_PATH}?filters=${encodeURIComponent(ADDON_PLAN_FILTERS)}`,
    { headers: authHeaders() },
  );
  const payload = (await response.json().catch(() => ({}))) as {
    data?: unknown;
    result?: { data?: unknown };
  };
  if (!response.ok) throw new Error('Unable to load add-on');
  const rows = payload.data ?? payload.result?.data ?? [];
  return Array.isArray(rows) ? (rows as Record<string, unknown>[]) : [];
}

export async function getCompanyAddons(facilityId?: number | string): Promise<CompanyAddon[]> {
  const plans = await fetchAddonPlans();
  const addons = plans.map(toAddon);
  if (!addons.some((addon) => addon.addon_key === OIG_CHECKS_ADDON_KEY)) return addons;
  const state = await fetchOigCompanyState(facilityId);
  return addons.map((addon) => withOigState(addon, state));
}

export async function getCompanyAddon(
  addonId: number | string,
  facilityId?: number | string,
): Promise<CompanyAddonDetail | undefined> {
  const plans = await fetchAddonPlans();
  const matchPlan = plans.find((plan) => String(plan.id) === String(addonId));
  if (!matchPlan) return undefined;
  const base = toAddon(matchPlan);
  const state = base.addon_key === OIG_CHECKS_ADDON_KEY ? await fetchOigCompanyState(facilityId) : null;
  const addon = withOigState(base, state);
  const active = addon.subscription_state === 'active';
  return {
    ...addon,
    tiers: tiersFor(matchPlan, plans),
    subscription: active
      ? { status: 'active', tier_name: addon.current_tier_name ?? null, usage: addon.usage ?? null }
      : null,
    aggregate: addon.usage
      ? { remaining: addon.usage.remaining, pool_remaining: addon.usage.remaining }
      : null,
    requests: [],
  };
}

export async function resolveOigChecksAddonId(
  facilityId?: number | string,
): Promise<string | undefined> {
  // Catalog only: no live capacity lookup needed to resolve the add-on id.
  void facilityId;
  const addons = (await fetchAddonPlans()).map(toAddon);
  const match =
    addons.find((addon) => addon.addon_key === OIG_CHECKS_ADDON_KEY) ||
    addons.find((addon) => OIG_PLAN_IDS.has(Number(addon.id)));
  return match ? String(match.id) : String(OIG_ADDON_BLOCKS[0].planId);
}
